import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Flag01Icon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons/button'
import { TextArea } from '@/components/base/textarea/textarea'
import { cn } from '@/lib/utils'
import type { PartnerReview } from '@/types/conversation'

const MAX_REVIEW_COMMENT_LENGTH = 500

export interface TranscriptReviewProps {
  /** Raw transcript indices of assistant turns that can be reviewed */
  reviewableIndices: number[]
  /** Existing reviews, keyed by raw transcript index via message_index */
  reviews: PartnerReview[]
  /** Submits one review; should throw on failure so the form stays open */
  onSubmit: (input: {
    messageIndex: number
    flagged: boolean
    comment: string
  }) => Promise<void>
}

export function ReviewBanner({ reviews, className }: { reviews: PartnerReview[]; className?: string }) {
  const { t } = useTranslation()
  const flaggedCount = reviews.filter((entry) => entry.flagged).length

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-200',
        className
      )}
    >
      <Flag01Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {t('resources.conversations.transcript.review.banner')}
        {flaggedCount > 0 &&
          ` · ${t('resources.conversations.transcript.review.banner_flagged', { count: flaggedCount })}`}
      </span>
    </div>
  )
}

export function ReviewFlagButton({
  isFlagged,
  onPress,
  className,
}: {
  isFlagged: boolean
  onPress: () => void
  className?: string
}) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={onPress}
      title={t('resources.conversations.transcript.review.flag')}
      className={cn(
        'rounded p-1 transition-colors',
        isFlagged
          ? 'text-red-500'
          : 'text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400',
        className
      )}
    >
      <Flag01Icon className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only">{t('resources.conversations.transcript.review.flag')}</span>
    </button>
  )
}

interface ReviewFormProps {
  existingReview?: PartnerReview
  onCancel: () => void
  /** Should throw on failure so the form stays open for a retry */
  onSubmit: (flagged: boolean, comment: string) => Promise<void>
}

export function ReviewForm({ existingReview, onCancel, onSubmit }: ReviewFormProps) {
  const { t } = useTranslation()
  const [comment, setComment] = useState(existingReview?.comment ?? '')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = async (flagged: boolean, value: string) => {
    setIsSubmitting(true)
    try {
      await onSubmit(flagged, value)
    } catch {
      // Error toast is shown by the caller; keep the form open for a retry
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full min-w-64 rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
      <TextArea
        value={comment}
        onChange={setComment}
        maxLength={MAX_REVIEW_COMMENT_LENGTH}
        rows={2}
        autoFocus
        placeholder={t(
          'resources.conversations.transcript.review.comment_placeholder'
        )}
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-gray-400">
          {comment.length}/{MAX_REVIEW_COMMENT_LENGTH}
        </span>
        <div className="flex items-center gap-2">
          {existingReview?.flagged && (
            <Button
              color="tertiary"
              size="sm"
              isDisabled={isSubmitting}
              onClick={() => submit(false, '')}
            >
              {t('resources.conversations.transcript.review.remove')}
            </Button>
          )}
          <Button
            color="tertiary"
            size="sm"
            isDisabled={isSubmitting}
            onClick={onCancel}
          >
            {t('resources.conversations.transcript.review.cancel')}
          </Button>
          <Button
            color="primary-destructive"
            size="sm"
            isDisabled={!comment.trim() || isSubmitting}
            isLoading={isSubmitting}
            onClick={() => submit(true, comment.trim())}
          >
            {t('resources.conversations.transcript.review.submit')}
          </Button>
        </div>
      </div>
    </div>
  )
}
